/**
 * RootCause Service Layer
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';
import { RootCauseFormSchema, UpdateRootCauseSchema } from '@repo/contracts';

export class RootCauseService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            const body = response;
            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                uniqueValues: body.uniqueValues || {},
                message: body.message || 'Kök neden listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch root-cause list');
        }
    }

    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch root-cause');
        }
    }

    async create(data) {
        const validation = RootCauseFormSchema.safeParse(data);
        if (!validation.success) {
            const errorMessage = validation.error.errors[0].message;
            throw new ApiError(errorMessage, 400, 'VALIDATION_ERROR');
        }

        try {
            const response = await this.api.create(validation.data);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Kök neden oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create root-cause');
        }
    }

    async update(id, data) {
        const validation = UpdateRootCauseSchema.safeParse(data);
        if (!validation.success) {
            const errorMessage = validation.error.errors[0].message;
            throw new ApiError(errorMessage, 400, 'VALIDATION_ERROR');
        }

        try {
            const response = await this.api.update(id, validation.data);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Kök neden güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update root-cause');
        }
    }

    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Kök neden silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete root-cause');
        }
    }

    async bulkDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);

            return {
                success: true,
                data: response.data ?? response,
                message: response.message || `${ids.length} öğe silindi`,
                deletedCount: response.deletedCount,
                blockedCount: response.blockedCount,
                blocked: response.blocked,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk delete root-causes');
        }
    }

    async batchScope(payload) {
        try {
            const response = await this.api.batchScope(payload);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Kapsam güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to batch update scope');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error.originalError ?? error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
